/* eslint-disable react/jsx-props-no-spreading */
import React, { useState } from 'react';
import PropTypes from 'prop-types';
import {
  Box, LinearProgress, Tooltip,
  Table, TableBody, TableContainer, Paper, TableHead, TableRow,
  TableFooter, TablePagination,
} from '@material-ui/core';
import CheckCircleIcon from '@material-ui/icons/CheckCircle';
import CancelIcon from '@material-ui/icons/Cancel';
import { ClipboardDocumentIcon, CheckIcon } from '@heroicons/react/24/outline';
import moment from 'moment';
import { StyledTableCell, StyledTableRow } from '../../../components/utils/StyledTable';
import TablePaginationActions from '../../../components/utils/TablePaginationActions';
import PresenceHistoryGraph from './PresenceHistoryGraph';

/**
 * Renders a payload summary value with a copy-to-clipboard icon.
 * The text is truncated visually but the full value is copied/shown on hover.
 */
const PayloadSummaryCell = ({ value }) => {
  const [copied, setCopied] = useState(false);

  if (!value || value === 'NA') {
    return <span className="text-gray-400">NA</span>;
  }

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(value);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch (err) {
      // Fallback for environments without clipboard API
      const textarea = document.createElement('textarea');
      textarea.value = value;
      document.body.appendChild(textarea);
      textarea.select();
      document.execCommand('copy');
      document.body.removeChild(textarea);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    }
  };

  return (
    <div className="flex items-center gap-2 max-w-xs">
      <Tooltip title={value} placement="top">
        <span className="block truncate text-sm text-gray-800" style={{ maxWidth: 240 }}>
          {value}
        </span>
      </Tooltip>
      <Tooltip title={copied ? 'Copied!' : 'Copy to clipboard'} placement="top">
        <button
          type="button"
          onClick={handleCopy}
          aria-label="Copy payload summary"
          className="flex-shrink-0 p-1 rounded text-gray-400 hover:text-indigo-600 hover:bg-indigo-50 focus:outline-none focus:ring-2 focus:ring-indigo-400 transition-colors"
        >
          {copied
            ? <CheckIcon className="h-4 w-4 text-green-600" />
            : <ClipboardDocumentIcon className="h-4 w-4" />}
        </button>
      </Tooltip>
    </div>
  );
};

PayloadSummaryCell.propTypes = {
  value: PropTypes.string,
};

PayloadSummaryCell.defaultProps = {
  value: '',
};

/**
 * IAM identifier prefixes that indicate BLE connection (AWS IAM credentials)
 * @see https://docs.aws.amazon.com/IAM/latest/UserGuide/reference_identifiers.html#identifiers-prefixes
 */
const IAM_PREFIXES = [
  'AIDA', // IAM user
  'AROA', // IAM role
  'ASIA', // STS temporary credentials
  'ABIA', // AWS STS service bearer token
  'ACCA', // Context-specific credential
  'AGPA', // IAM group
  'AIPA', // EC2 instance profile
  'AKIA', // IAM access key
  'ANPA', // IAM managed policy
  'ANVA', // IAM managed policy version
  'APKA', // IAM public key
  'ASCA', // IAM certificate
];

/**
 * Determines the connection type based on principalID
 * @param {string} principalId - The principal ID from the connectivity event
 * @returns {string} 'WIFI' if certificate-based, 'BLE' if AWS IAM credential, or 'Unknown'
 * 
 * @see https://docs.aws.amazon.com/iot/latest/developerguide/life-cycle-events.html
 * @see https://docs.aws.amazon.com/iot/latest/apireference/API_DescribeCertificate.html
 * 
 * WIFI connections use certificate IDs: 64-character hex strings
 * BLE connections use IAM credentials: Start with IAM prefixes (AIDA, AROA, ASIA, etc.)
 */
const getConnectionType = (principalId) => {
  if (!principalId) return 'Unknown';
  
  // Check for IAM credential prefixes (BLE connection)
  const upperPrincipalId = principalId.toUpperCase();
  const hasIamPrefix = IAM_PREFIXES.some((prefix) => upperPrincipalId.startsWith(prefix));
  if (hasIamPrefix) {
    return 'BLE';
  }
  
  // Certificate IDs are 64-character hex strings (WIFI connection)
  const hexPattern = /^[a-f0-9]{64}$/i;
  if (hexPattern.test(principalId)) {
    return 'WIFI';
  }
  
  return 'Unknown';
};

/**
 * History context identifiers. Kept in sync with HISTORY_CONTEXTS in DeviceOverview.js.
 */
const CONTEXT = {
  CONNECTIVITY: 'CONNECTIVITY',
  DEVICE: 'DEVICE',
  MODULE: 'MODULE',
  GATEWAY: 'GATEWAY',
  SETTINGS: 'SETTINGS',
  MODULE_SETTINGS: 'MODULE_SETTINGS',
};

/**
 * Column definitions for the history table. Each column declares the contexts in
 * which it is relevant; `contexts: null` means the column applies to every context.
 * Columns such as Status, Event Type and Connection Type only carry meaningful data
 * for the Connectivity context, so they are hidden for device/module/gateway/settings
 * history instead of being padded with placeholder values.
 */
const HISTORY_COLUMNS = [
  {
    key: 'status',
    header: 'Status',
    contexts: [CONTEXT.CONNECTIVITY],
    render: (status) => (
      <Box display="flex" alignItems="center">
        {status?.type === 'connected' ? (
          <CheckCircleIcon style={{ color: 'green', marginRight: 8 }} />
        ) : (
          <CancelIcon style={{ color: 'red', marginRight: 8 }} />
        )}
        {status?.type}
      </Box>
    ),
  },
  {
    key: 'eventType',
    header: 'Event Type',
    contexts: [CONTEXT.CONNECTIVITY],
    render: (status) => status?.eventType || 'unknown',
  },
  {
    key: 'deviceSerial',
    header: 'Device Serial',
    contexts: null,
    render: (status) => status?.deviceSerial || 'NA',
  },
  {
    key: 'moduleId',
    header: 'Module ID',
    contexts: [CONTEXT.MODULE, CONTEXT.MODULE_SETTINGS],
    render: (status) => status?.moduleId || 'NA',
  },
  {
    key: 'gatewayId',
    header: 'Gateway ID',
    contexts: [CONTEXT.GATEWAY],
    render: (status) => status?.gatewayId || 'NA',
  },
  {
    key: 'payloadSummary',
    header: 'Payload Summary',
    contexts: null,
    render: (status) => <PayloadSummaryCell value={status?.payloadSummary || status?.reason || 'NA'} />,
  },
  {
    key: 'connectionType',
    header: 'Connection Type',
    contexts: [CONTEXT.CONNECTIVITY],
    render: (status) => (
      <Box display="flex" alignItems="center">
        {getConnectionType(status?.principalID)}
      </Box>
    ),
  },
  {
    key: 'updatedLocal',
    header: 'Updated timestamp (Local)',
    contexts: null,
    render: (status) => (status?.updateAt ? moment(status.updateAt).format('YYYY-MM-DD HH:mm:ss') : 'NA'),
  },
  {
    key: 'updatedUtc',
    header: 'Updated timestamp (UTC)',
    contexts: null,
    render: (status) => (status?.updateAt ? moment(status.updateAt).utc().format('YYYY-MM-DD HH:mm:ss') : 'NA'),
  },
];

/**
 * Returns the columns that apply to the given history context.
 */
const getVisibleColumns = (historyContext) => HISTORY_COLUMNS.filter(
  (column) => column.contexts === null || column.contexts.includes(historyContext),
);

const ConnectivityStatusHistory = ({
  data,
  isLoading,
  page,
  rowsPerPage,
  handleChangePage,
  connectivityStatusHistoryToken,
  presenceHistoryData,
  isLoadingPresenceHistory,
  selectedStartDate,
  selectedEndDate,
  tableControls,
  historyContext,
}) => {
  const hasMorePages = Boolean(connectivityStatusHistoryToken);
  const paginationCount = hasMorePages ? -1 : 0;
  const visibleColumns = getVisibleColumns(historyContext);
  const columnCount = visibleColumns.length;

  return (
    <>
    {/* Presence History Graph - shows connectivity for the selected date range */}
    <PresenceHistoryGraph 
      data={presenceHistoryData} 
      isLoading={isLoadingPresenceHistory}
      startDate={selectedStartDate}
      endDate={selectedEndDate}
    />

    {tableControls}

    <TableContainer component={Paper}>
    <Table aria-label="custom pagination table">
      <TableHead>
        <TableRow>
          {visibleColumns.map((column) => (
            <StyledTableCell key={column.key}>{column.header}</StyledTableCell>
          ))}
        </TableRow>
      </TableHead>
      <TableBody>
        {data?.map((status, idx) => (
          status == null ? (
            <StyledTableRow key={`null-row-${idx}`}>
              <StyledTableCell colSpan={columnCount}>null</StyledTableCell>
            </StyledTableRow>
          ) : (
            <StyledTableRow key={`${status?.updateAt || status?.updatedAt || 'no-timestamp'}-${status?.type || 'no-type'}-${idx}`}>
              {visibleColumns.map((column) => (
                <StyledTableCell key={column.key}>{column.render(status)}</StyledTableCell>
              ))}
            </StyledTableRow>
          )
        ))}
      </TableBody>
      <TableFooter>
        <TableRow key="paginationFooter">
          <TablePagination
            rowsPerPageOptions={[rowsPerPage]}
            colSpan={columnCount}
            rowsPerPage={rowsPerPage}
            page={page}
            SelectProps={{
              inputProps: { 'aria-label': 'rows per page' },
              native: true,
            }}
            onChangePage={handleChangePage}
            ActionsComponent={(props) => (
              <TablePaginationActions
                {...props}
                onChangePage={handleChangePage}
                count={paginationCount}
                page={page}
                rowsPerPage={rowsPerPage}
              />
            )}
            labelDisplayedRows={() => {
              if (hasMorePages) return `Page ${page + 1} • ${data.length} rows (more pages available)`;
              return `Page ${page + 1} • ${data.length} rows`;
            }}
            count={paginationCount}
          />
        </TableRow>
      </TableFooter>
    </Table>
    {isLoading && <LinearProgress />}
  </TableContainer>
    </>
  );
};

ConnectivityStatusHistory.propTypes = {
  data: PropTypes.arrayOf(
    PropTypes.shape({
      type: PropTypes.string.isRequired,
      eventType: PropTypes.string,
      deviceSerial: PropTypes.string,
      moduleId: PropTypes.string,
      gatewayId: PropTypes.string,
      payloadSummary: PropTypes.string,
      reason: PropTypes.string,
      updateAt: PropTypes.string,
      principalID: PropTypes.string,
    }),
  ).isRequired,
  isLoading: PropTypes.bool.isRequired,
  page: PropTypes.number.isRequired,
  rowsPerPage: PropTypes.number.isRequired,
  handleChangePage: PropTypes.func.isRequired,
  connectivityStatusHistoryToken: PropTypes.string,
  presenceHistoryData: PropTypes.arrayOf(
    PropTypes.shape({
      type: PropTypes.string,
      updateAt: PropTypes.string,
      principalID: PropTypes.string,
    }),
  ),
  isLoadingPresenceHistory: PropTypes.bool,
  selectedStartDate: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.instanceOf(Date),
    PropTypes.object, // moment object
  ]),
  selectedEndDate: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.instanceOf(Date),
    PropTypes.object, // moment object
  ]),
  tableControls: PropTypes.node,
  historyContext: PropTypes.string,
};

ConnectivityStatusHistory.defaultProps = {
  connectivityStatusHistoryToken: '',
  presenceHistoryData: [],
  isLoadingPresenceHistory: false,
  selectedStartDate: null,
  selectedEndDate: null,
  tableControls: null,
  historyContext: CONTEXT.CONNECTIVITY,
};

export default ConnectivityStatusHistory;
